import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ArrowDownUp, CalendarIcon, Check, ChevronDown, X } from 'lucide-react';
import type { ActivityType, ActivityFeedFilters } from 'shared/src/api/activity-feed-api-types';
import { Button } from '@/components/forms/ui/button';
import { Calendar } from '@/components/forms/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/forms/ui/popover';
import { AsyncTypeahead } from '@/components/forms/ui/AsyncTypeahead';
import { client } from '@/utils/trpc-client';
import { cn } from '@/lib/utils';

type ActivityFilterBarProps = {
  availableActivityTypes: ActivityType[];
  filters: ActivityFeedFilters;
  onFilterChange: (filters: ActivityFeedFilters) => void;
  sortDirection: 'asc' | 'desc';
  onSortDirectionChange: (dir: 'asc' | 'desc') => void;
  onClearFilters: () => void;
  initialLabels?: { actor?: string; campaign?: string; account?: string; lineItem?: string };
};

export function ActivityFilterBar({
  availableActivityTypes,
  filters,
  onFilterChange,
  sortDirection,
  onSortDirectionChange,
  onClearFilters,
  initialLabels
}: ActivityFilterBarProps) {
  const [selectedLabels, setSelectedLabels] = useState<{
    actor?: string;
    campaign?: string;
    account?: string;
    lineItem?: string;
  }>(initialLabels ?? {});

  // Reset labels when filters are cleared externally (e.g. parent calls setFilters directly)
  useEffect(() => {
    if (!filters.actorId && !filters.campaignId && !filters.accountId && !filters.lineItemId) {
      setSelectedLabels({});
    }
  }, [filters.actorId, filters.campaignId, filters.accountId, filters.lineItemId]);

  const hasActiveFilters =
    (filters.activityTypes?.length ?? 0) > 0 ||
    !!filters.dateFrom ||
    !!filters.dateTo ||
    !!filters.actorId ||
    !!filters.actorName ||
    !!filters.campaignId ||
    !!filters.accountId ||
    !!filters.lineItemId;

  const handleTypeToggle = useCallback(
    (type: ActivityType) => {
      const current = filters.activityTypes ?? [];
      const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
      onFilterChange({ ...filters, activityTypes: next.length > 0 ? next : undefined });
    },
    [filters, onFilterChange]
  );

  const handleDateFromChange = useCallback(
    (date: Date | undefined) => {
      onFilterChange({ ...filters, dateFrom: date ? format(date, 'yyyy-MM-dd') : undefined });
    },
    [filters, onFilterChange]
  );

  const handleDateToChange = useCallback(
    (date: Date | undefined) => {
      onFilterChange({ ...filters, dateTo: date ? format(date, 'yyyy-MM-dd') : undefined });
    },
    [filters, onFilterChange]
  );

  const handleSortToggle = useCallback(() => {
    onSortDirectionChange(sortDirection === 'desc' ? 'asc' : 'desc');
  }, [sortDirection, onSortDirectionChange]);

  const handleClearFilters = useCallback(() => {
    setSelectedLabels({});
    onClearFilters();
  }, [onClearFilters]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <ActivityTypeMultiSelect
          availableTypes={availableActivityTypes}
          selectedTypes={filters.activityTypes ?? []}
          onToggle={handleTypeToggle}
        />

        <DatePickerFilter label="From" value={filters.dateFrom} onChange={handleDateFromChange} />

        <DatePickerFilter label="To" value={filters.dateTo} onChange={handleDateToChange} />

        <Button variant="outline" size="sm" onClick={handleSortToggle} className="gap-1.5">
          <ArrowDownUp className="size-3.5" />
          {sortDirection === 'desc' ? 'Newest' : 'Oldest'}
        </Button>

        {hasActiveFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-muted-foreground gap-1.5">
            <X className="size-3.5" />
            Clear Filters
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <AsyncTypeahead
          placeholder="Actor..."
          className="w-48"
          selectedLabel={selectedLabels.actor}
          searchFn={async search => client.activitySearchActors.query({ search })}
          onSelect={item => {
            setSelectedLabels(prev => ({ ...prev, actor: item.label }));
            onFilterChange({ ...filters, actorId: item.id, actorName: undefined });
          }}
          onClear={() => {
            setSelectedLabels(prev => ({ ...prev, actor: undefined }));
            onFilterChange({ ...filters, actorId: undefined });
          }}
        />

        <AsyncTypeahead
          placeholder="Campaign..."
          className="w-48"
          selectedLabel={selectedLabels.campaign}
          searchFn={async search => client.activitySearchCampaigns.query({ search })}
          onSelect={item => {
            setSelectedLabels(prev => ({ ...prev, campaign: item.label }));
            onFilterChange({ ...filters, campaignId: item.id });
          }}
          onClear={() => {
            setSelectedLabels(prev => ({ ...prev, campaign: undefined }));
            onFilterChange({ ...filters, campaignId: undefined });
          }}
        />

        <AsyncTypeahead
          placeholder="Account..."
          className="w-48"
          selectedLabel={selectedLabels.account}
          searchFn={async search => client.activitySearchAccounts.query({ search })}
          onSelect={item => {
            setSelectedLabels(prev => ({ ...prev, account: item.label }));
            onFilterChange({ ...filters, accountId: item.id });
          }}
          onClear={() => {
            setSelectedLabels(prev => ({ ...prev, account: undefined }));
            onFilterChange({ ...filters, accountId: undefined });
          }}
        />

        <AsyncTypeahead
          placeholder="Line Item..."
          className="w-48"
          selectedLabel={selectedLabels.lineItem}
          searchFn={async search => client.activitySearchLineItems.query({ search })}
          onSelect={item => {
            setSelectedLabels(prev => ({ ...prev, lineItem: item.label }));
            onFilterChange({ ...filters, lineItemId: item.id });
          }}
          onClear={() => {
            setSelectedLabels(prev => ({ ...prev, lineItem: undefined }));
            onFilterChange({ ...filters, lineItemId: undefined });
          }}
        />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Private helpers
 * --------------------------------------------------------------------------- */

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  campaign_change: 'Campaign',
  strategy_change: 'Strategy',
  line_item_change: 'Line Item',
  platform_entity_change: 'Platform Entity',
  rate_card_change: 'Rate Card',
  zoho_note: 'Zoho Note',
  creative_request: 'Creative Request'
};

type ActivityTypeMultiSelectProps = {
  availableTypes: ActivityType[];
  selectedTypes: ActivityType[];
  onToggle: (type: ActivityType) => void;
};

function ActivityTypeMultiSelect({
  availableTypes,
  selectedTypes,
  onToggle
}: ActivityTypeMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const label =
    selectedTypes.length === 0
      ? 'Activity Type'
      : selectedTypes.length === 1
        ? ACTIVITY_TYPE_LABELS[selectedTypes[0]]
        : `${selectedTypes.length} types`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          {label}
          <ChevronDown className="size-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-1" align="start">
        {availableTypes.map(type => {
          const selected = selectedTypes.includes(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => onToggle(type)}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                'hover:bg-accent hover:text-accent-foreground'
              )}>
              <span
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                  selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                )}>
                {selected ? <Check className="size-3" /> : null}
              </span>
              {ACTIVITY_TYPE_LABELS[type]}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

type DatePickerFilterProps = {
  label: string;
  value: string | undefined;
  onChange: (date: Date | undefined) => void;
};

function DatePickerFilter({ label, value, onChange }: DatePickerFilterProps) {
  const dateValue = value ? new Date(value + 'T00:00:00') : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5', !value && 'text-muted-foreground')}>
          <CalendarIcon className="size-3.5" />
          {value ? format(dateValue!, 'MMM d, yyyy') : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={dateValue} onSelect={onChange} />
      </PopoverContent>
    </Popover>
  );
}
